import type { MessengerMessage } from '@/api/client';
import { MESSENGER_DIRECT_THREAD_CACHE_MAX_MESSAGES } from '@/components/Messenger/cache/directThreadCacheConstants';
import { isMessengerOptimisticMessageId } from '@/constants/messengerOptimistic';

export type DirectThreadWindowTrimSide = 'start' | 'end';

export type DirectThreadWindowTrimResult = {
  messages: MessengerMessage[];
  trimmedFromStart: boolean;
  trimmedFromEnd: boolean;
};

/**
 * Ограничивает окно сообщений в памяти.
 * - `end` — после prepend older (срезаем новые с хвоста);
 * - `start` — после append newer (срезаем старые с головы).
 */
export function trimDirectThreadMessageWindow(
  messages: MessengerMessage[],
  maxMessages: number = MESSENGER_DIRECT_THREAD_CACHE_MAX_MESSAGES,
  trimExcessFrom: DirectThreadWindowTrimSide
): DirectThreadWindowTrimResult {
  if (messages.length <= maxMessages) {
    return { messages, trimmedFromStart: false, trimmedFromEnd: false };
  }
  const excess = messages.length - maxMessages;
  if (trimExcessFrom === 'end') {
    return {
      messages: messages.slice(0, maxMessages),
      trimmedFromStart: false,
      trimmedFromEnd: true,
    };
  }
  return {
    messages: messages.slice(excess),
    trimmedFromStart: true,
    trimmedFromEnd: false,
  };
}

export function mergeOlderPageIntoWindow(
  current: MessengerMessage[],
  incoming: MessengerMessage[]
): MessengerMessage[] {
  if (incoming.length === 0) return current;
  const existingIds = new Set(current.map((m) => m.id));
  const newOlder = incoming.filter((m) => !existingIds.has(m.id));
  if (newOlder.length === 0) return current;
  return [...newOlder, ...current];
}

export function mergeNewerPageIntoWindow(
  current: MessengerMessage[],
  incoming: MessengerMessage[]
): MessengerMessage[] {
  if (incoming.length === 0) return current;
  const existingIds = new Set(current.map((m) => m.id));
  const newNewer = incoming.filter((m) => !existingIds.has(m.id));
  if (newNewer.length === 0) return current;
  return [...current, ...newNewer];
}

export type DirectThreadWindowBounds = {
  messages: { id: string }[];
  oldestLoadedMessageId: string | null;
  newestLoadedMessageId: string | null;
  hasMoreOlder: boolean;
  hasMoreNewer: boolean;
};

/**
 * Края окна — только сообщения, уже сохранённые на сервере: оптимистичный `opt/…` id
 * (отправка в пути или упавшая) не годится ни как курсор пагинации, ни как граница загруженного.
 */
export function windowServerHeadId(messages: readonly { id: string }[]): string | null {
  for (const m of messages) {
    if (!isMessengerOptimisticMessageId(m.id)) return m.id;
  }
  return null;
}

export function windowServerTailId(messages: readonly { id: string }[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const id = messages[i]!.id;
    if (!isMessengerOptimisticMessageId(id)) return id;
  }
  return null;
}

/** Есть разрыв между головой окна и самым старым уже загруженным id. */
export function hasOlderGapInWindow(bounds: DirectThreadWindowBounds): boolean {
  const headId = windowServerHeadId(bounds.messages);
  return Boolean(headId && bounds.oldestLoadedMessageId && headId !== bounds.oldestLoadedMessageId);
}

/** Есть разрыв между хвостом окна и самым новым уже загруженным id (после trim снизу). */
export function hasNewerGapInWindow(bounds: DirectThreadWindowBounds): boolean {
  const tailId = windowServerTailId(bounds.messages);
  return Boolean(tailId && bounds.newestLoadedMessageId && tailId !== bounds.newestLoadedMessageId);
}

export function effectiveHasMoreOlder(bounds: DirectThreadWindowBounds): boolean {
  return bounds.hasMoreOlder || hasOlderGapInWindow(bounds);
}

export function effectiveHasMoreNewer(bounds: DirectThreadWindowBounds): boolean {
  return bounds.hasMoreNewer || hasNewerGapInWindow(bounds);
}

/**
 * Курсор `before` для loadOlder: при разрыве окна — от видимой головы, иначе от oldestLoaded.
 */
export function resolveOlderPaginationCursor(bounds: DirectThreadWindowBounds): string | null {
  const headId = windowServerHeadId(bounds.messages);
  const oldestId = bounds.oldestLoadedMessageId;
  if (headId && oldestId && headId !== oldestId) return headId;
  return oldestId ?? headId;
}

/**
 * Курсор `after` для loadNewer: при разрыве окна — от видимого хвоста, иначе от newestLoaded.
 */
export function resolveNewerPaginationCursor(bounds: DirectThreadWindowBounds): string | null {
  const tailId = windowServerTailId(bounds.messages);
  const newestId = bounds.newestLoadedMessageId;
  if (tailId && newestId && tailId !== newestId) return tailId;
  return newestId ?? tailId;
}
